import type { Price } from 'types/capi/shared';

import {
  type ContractStockStatus,
  type StockStatus,
  toContractStockStatus
} from '~/domain/stockStatus';

import ImpressionEvent, { type ImpressionType } from './ImpressionEvent';
import type {
  NotificationElement,
  NotificationPlacement
} from './NotificationInteractionEvent';

const NO_PRICE = -1;

export type NotificationImpressionElement = Extract<
  NotificationElement,
  'alert_edit' | 'confirm_email' | 'notify_me'
>;

export type NotificationImpressionProperties = {
  element: NotificationImpressionElement;
  placement: NotificationPlacement;
  price_currency: string;
  price_regular: number;
  price_sale?: number;
  product_id: string;
  stock_status?: ContractStockStatus;
};

type EventData = {
  element: NotificationImpressionElement;
  guestId?: string;
  isBot: boolean;
  placement: NotificationPlacement;
  price: Price;
  productId: number;
  stockStatus?: StockStatus;
};

// The seen-side of NotificationInteractionEvent, on the same keys so a button's impressions and
// taps join. The element is part of the dedupe identity, so a button that changes what it offers
// mid-view (Notify me becoming Confirm email) is a second impression.
class NotificationImpressionEvent extends ImpressionEvent<
  EventData,
  NotificationImpressionProperties
> {
  protected get impressionType(): ImpressionType {
    return 'notification';
  }

  protected getImpressionProperties(): NotificationImpressionProperties {
    const { stockStatus } = this.data;

    return {
      element: this.data.element,
      placement: this.data.placement,
      price_currency: this.data.price.currency,
      price_regular: this.data.price.regular ?? NO_PRICE,
      price_sale: this.data.price.sale ?? undefined,
      product_id: String(this.data.productId),
      stock_status: stockStatus ? toContractStockStatus(stockStatus) : undefined
    };
  }
}

export default NotificationImpressionEvent;
